/**
 * LoadingSpinner Component
 * 
 * Loading indicators and skeletons
 * 
 * Version: 6.2.0
 */
import React from 'react'
import './LoadingSpinner.css'

type SpinnerSize = 'small' | 'medium' | 'large'
type SpinnerVariant = 'spinner' | 'dots' | 'pulse' | 'bars'

interface LoadingSpinnerProps {
  size?: SpinnerSize
  variant?: SpinnerVariant
  color?: string
  text?: string
  className?: string
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'medium',
  variant = 'spinner',
  color,
  text,
  className = ''
}) => {
  const sizeClass = `size-${size}`
  const style = color ? { '--spinner-color': color } as React.CSSProperties : undefined

  return (
    <div className={`loading-spinner ${sizeClass} ${className}`} style={style}>
      {variant === 'spinner' && (
        <div className="spinner-ring">
          <div />
          <div />
          <div />
          <div />
        </div>
      )}

      {variant === 'dots' && (
        <div className="spinner-dots">
          <div className="dot" />
          <div className="dot" />
          <div className="dot" />
        </div>
      )}

      {variant === 'pulse' && (
        <div className="spinner-pulse">
          <div className="pulse-ring" />
          <div className="pulse-core">📈</div>
        </div>
      )}

      {variant === 'bars' && (
        <div className="spinner-bars">
          <div className="bar" />
          <div className="bar" />
          <div className="bar" />
          <div className="bar" />
        </div>
      )}

      {text && <span className="spinner-text">{text}</span>}
    </div>
  )
}

// Full screen loading overlay
interface LoadingOverlayProps {
  isLoading: boolean
  children: React.ReactNode
  text?: string
  blur?: boolean
  className?: string
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isLoading,
  children,
  text = 'Loading...',
  blur = true,
  className = ''
}) => {
  return (
    <div className={`loading-overlay-container ${className}`}>
      {children}
      {isLoading && (
        <div className={`loading-overlay ${blur ? 'blur' : ''}`}>
          <LoadingSpinner size="large" variant="spinner" text={text} />
        </div>
      )}
    </div>
  )
}

// Inline loading placeholder for content
interface ContentLoaderProps {
  type?: 'text' | 'avatar' | 'image' | 'card' | 'button'
  width?: string
  height?: string
  className?: string
}

export const ContentLoader: React.FC<ContentLoaderProps> = ({
  type = 'text',
  width = '100%',
  height,
  className = ''
}) => {
  return (
    <div 
      className={`content-loader ${type} ${className}`}
      style={{ width, height }}
    />
  )
}

export default LoadingSpinner